import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import type { User } from '@auth0/auth0-react'
import { useMutation } from '@apollo/client/react'
import { useUserAccount } from '../hooks/useUserAccount'
import { IonContent, IonPage, IonSpinner, useIonRouter } from '@ionic/react'
import { UPDATE_USER_ACCOUNT } from '../graphql/mutations/updateUserAccount'
import type { UpdateUserAccountMutation, UpdateUserAccountMutationVariables } from '../types/graphql-generated'

function extractNameFromAuth0User(user: User): { firstName?: string; lastName?: string; fullName: string } | null {
  const firstName = user.given_name || (user as Record<string, unknown>).first_name as string | undefined
  const lastName = user.family_name || (user as Record<string, unknown>).last_name as string | undefined
  const fullName = [firstName, lastName].filter(Boolean).join(' ')

  return fullName ? { firstName, lastName, fullName } : null
}

export const AuthCallback = () => {
  const ionRouter = useIonRouter()
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0()
  const { userAccount, loading: accountLoading, error: accountError } = useUserAccount()
  const [updateUserAccount] = useMutation<UpdateUserAccountMutation, UpdateUserAccountMutationVariables>(
    UPDATE_USER_ACCOUNT
  )

  useEffect(() => {
    const handleCallback = async () => {
      // Wait for Auth0 and user account query to complete
      if (authLoading || accountLoading) {
        return
      }

      // If there's an error loading user account, don't redirect
      // Let the AppInitializer handle the error display
      if (accountError) {
        return
      }

      // If not authenticated, redirect to landing
      if (!isAuthenticated) {
        ionRouter.push('/landing', 'root', 'replace')
        return
      }

      // If user account doesn't exist yet, wait for it to be created
      // The backend automatically creates it on first authentication
      if (!userAccount) {
        // Account should be created automatically, but if it's still null after loading,
        // something went wrong - redirect to landing
        ionRouter.push('/landing', 'root', 'replace')
        return
      }

      // Check if profile is complete
      if (!userAccount.fullName) {
        // Attempt to auto-populate name from social login data (Apple, Google)
        // to avoid asking users to re-enter data already provided by the social provider
        const nameData = user ? extractNameFromAuth0User(user) : null
        if (nameData) {
          try {
            const result = await updateUserAccount({ variables: { input: nameData } })
            if (result.data?.updateUserAccount?.userAccount?.fullName) {
              ionRouter.push('/app', 'root', 'replace')
              return
            }
          } catch (err) {
            console.error('[AuthCallback] Failed to auto-update name from social login', err)
          }
        }
        ionRouter.push('/complete-profile', 'forward', 'replace')
      } else {
        ionRouter.push('/app', 'root', 'replace')
      }
    }

    handleCallback()
  }, [isAuthenticated, authLoading, userAccount, accountLoading, accountError, user, ionRouter, updateUserAccount])

  return (
    <IonPage>
      <IonContent className="ion-padding ion-text-center">
        <div className="flex flex-col items-center justify-center h-full">
          <IonSpinner name="crescent" />
          <p className="mt-4">Completando autenticación de usuario...</p>
        </div>
      </IonContent>
    </IonPage>
  )
}
